import { useState } from 'react';
import {
  Control,
  FieldErrors,
  UseFieldArrayReturn,
  useWatch,
} from 'react-hook-form';
import SortableList from '../../../../shared/sortable-list';
import FieldCard from './field-card';
import { LockIndex, NO_LOCKS } from '../label-locks';
import FieldTypeIcon from './field-type-icon';
import { newField } from './label-draft-mapper';
import {
  CreateLabelForm,
  FIELD_TYPE_CONFIG,
  LABEL_FIELD_TYPES,
  LABEL_LIMITS,
} from './label-schema';
import type { LabelFieldType } from '../../../../api/drive-labels-api';

import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import Divider from '@mui/material/Divider';
import IconButton from '@mui/material/IconButton';
import Paper from '@mui/material/Paper';
import Stack from '@mui/material/Stack';
import SvgIcon from '@mui/material/SvgIcon';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
import Add from '@mui/icons-material/Add';
import InfoOutlined from '@mui/icons-material/InfoOutlined';
import type { SvgIconProps } from '@mui/material/SvgIcon';

/** The empty-state "list details" outline icon from the design (not in MUI's icon set). */
function ListDetailsIcon(props: SvgIconProps) {
  return (
    <SvgIcon {...props}>
      <g
        fill="none"
        stroke="currentColor"
        strokeWidth={2}
        strokeLinecap="round"
        strokeLinejoin="round"
      >
        <path d="M13 5h8M13 9h5M13 15h8M13 19h5" />
        <rect x="3" y="4" width="6" height="6" rx="1" />
        <rect x="3" y="14" width="6" height="6" rx="1" />
      </g>
    </SvgIcon>
  );
}

interface StepAddFieldsProps {
  control: Control<CreateLabelForm>;
  errors: FieldErrors<CreateLabelForm>;
  fieldArray: UseFieldArrayReturn<CreateLabelForm, 'fields', 'rhfKey'>;
  onDragStateChange: (isDragging: boolean) => void;
  /** Edit Label: the label's Google rule locks. */
  locks?: LockIndex;
  /** Edit Label: opens the Rules step from a lock's popover. */
  onViewRules?: () => void;
}

export const DELETE_PUBLISHED_FIELD_NOTE =
  'Note: In order to delete published fields, first disable it.';

function disabledReason(
  type: LabelFieldType,
  fields: { type: LabelFieldType }[],
  driveEnabled: boolean
): string | null {
  if (fields.length >= LABEL_LIMITS.maxFields) {
    return `You have reached the maximum limit of fields (${LABEL_LIMITS.maxFields})`;
  }
  if (
    type === 'BADGE_LIST' &&
    fields.filter((f) => f.type === 'BADGE_LIST').length >=
      LABEL_LIMITS.maxBadgeLists
  ) {
    return 'Maximum of 1 badge list field is allowed';
  }
  if (!FIELD_TYPE_CONFIG[type].gmailSupported && !driveEnabled) {
    return 'Select Drive and Docs in Label Information to use this field';
  }
  return null;
}

export default function StepAddFields({
  control,
  errors,
  fieldArray,
  onDragStateChange,
  locks = NO_LOCKS,
  onViewRules,
}: StepAddFieldsProps) {
  const { fields: rhfFields, append, remove, move } = fieldArray;
  const [fields, driveEnabled] = useWatch({
    control,
    name: ['fields', 'driveEnabled'],
  });
  const [expandedKeys, setExpandedKeys] = useState<Set<string>>(new Set());

  const toggleExpanded = (key: string) =>
    setExpandedKeys((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });

  const handleAdd = (type: LabelFieldType) => {
    const field = newField(type, fields);
    append(field);
    setExpandedKeys((prev) => new Set(prev).add(field.key));
  };

  const badgeLimitReached =
    fields.filter((f) => f.type === 'BADGE_LIST').length >=
    LABEL_LIMITS.maxBadgeLists;

  const renderPaletteItem = (type: LabelFieldType) => {
    const reason = disabledReason(type, fields, driveEnabled);
    const config = FIELD_TYPE_CONFIG[type];
    return (
      <Box key={type}>
        <Paper
          variant="outlined"
          sx={{ px: 1.5, py: 1, opacity: reason ? 0.6 : 1 }}
        >
          <Stack direction="row" spacing={1} alignItems="center">
            <FieldTypeIcon type={type} color="action" />
            <Box sx={{ flex: 1, minWidth: 0 }}>
              <Typography variant="body2" sx={{ fontWeight: 500 }}>
                {config.label}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                {config.description}
              </Typography>
            </Box>
            <Tooltip title={reason ?? `Add ${config.label}`}>
              <span>
                <IconButton
                  color="primary"
                  aria-label={`Add ${config.label}`}
                  disabled={!!reason}
                  onClick={() => handleAdd(type)}
                >
                  <Add />
                </IconButton>
              </span>
            </Tooltip>
          </Stack>
        </Paper>
        {type === 'BADGE_LIST' && badgeLimitReached && (
          <Stack
            direction="row"
            spacing={0.5}
            alignItems="center"
            sx={{ mt: 0.5 }}
          >
            <InfoOutlined color="info" sx={{ fontSize: 16 }} />
            <Typography variant="caption" color="info.main">
              Maximum of 1 badge list field is allowed
            </Typography>
          </Stack>
        )}
      </Box>
    );
  };

  const gmailTypes = LABEL_FIELD_TYPES.filter(
    (t) => FIELD_TYPE_CONFIG[t].gmailSupported
  );
  const driveTypes = LABEL_FIELD_TYPES.filter(
    (t) => !FIELD_TYPE_CONFIG[t].gmailSupported
  );

  return (
    <Stack spacing={2}>
      <Box>
        <Typography variant="subtitle1" sx={{ fontWeight: 500 }}>
          Add Fields (optional)
        </Typography>
        <Typography variant="body2" color="text.secondary">
          Add fields to define information users will fill in when applying this
          label
        </Typography>
      </Box>
      <Divider />
      <Stack direction={{ xs: 'column', md: 'row' }} spacing={3}>
        <Stack spacing={1.5} sx={{ width: { md: 340 }, flexShrink: 0 }}>
          <Typography variant="body2" sx={{ fontWeight: 500 }}>
            Drive &amp; Gmail
          </Typography>
          {gmailTypes.map(renderPaletteItem)}
          <Typography variant="body2" sx={{ fontWeight: 500, pt: 1 }}>
            Drive &amp; Docs
          </Typography>
          {driveTypes.map(renderPaletteItem)}
        </Stack>

        <Divider orientation="vertical" flexItem />

        <Stack spacing={1.5} sx={{ flex: 1, minWidth: 0 }}>
          {rhfFields.length === 0 ? (
            <Stack
              spacing={3}
              alignItems="center"
              justifyContent="center"
              // Fills the column so it sits vertically centered next to the field types (design).
              sx={{ flex: 1, py: 4, textAlign: 'center' }}
            >
              <Box
                sx={{
                  width: 60,
                  height: 60,
                  borderRadius: '50%',
                  bgcolor: 'action.hover',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                }}
              >
                <ListDetailsIcon sx={{ color: 'text.secondary' }} />
              </Box>
              <Box>
                <Typography variant="body2" color="text.secondary">
                  Nothing to preview yet
                </Typography>
                <Typography variant="caption" color="text.disabled">
                  Pick a field type on the left to get started
                </Typography>
              </Box>
            </Stack>
          ) : (
            <>
              {fields.some((field) => field.published) && (
                <Typography variant="caption" color="text.secondary">
                  {DELETE_PUBLISHED_FIELD_NOTE}
                </Typography>
              )}
              <SortableList
                items={rhfFields}
                getId={(field) => field.key}
                onMove={move}
                onDragStateChange={onDragStateChange}
                gap={1.5}
                renderItem={(field, index, handle, isDragging) => (
                  <FieldCard
                    control={control}
                    errors={errors}
                    field={
                      fields.find((live) => live.key === field.key) ?? field
                    }
                    index={index}
                    expanded={expandedKeys.has(field.key)}
                    onToggle={() => toggleExpanded(field.key)}
                    onDelete={() => remove(index)}
                    handle={handle}
                    isDragging={isDragging}
                    onDragStateChange={onDragStateChange}
                    locks={locks}
                    onViewRules={onViewRules}
                  />
                )}
              />
            </>
          )}
          {rhfFields.length >= LABEL_LIMITS.maxFields && (
            <Alert severity="warning">
              You have reached the maximum limit of fields (
              {LABEL_LIMITS.maxFields})
            </Alert>
          )}
        </Stack>
      </Stack>
    </Stack>
  );
}
